import { AxiosResponse } from 'axios';
import axiosInstance from './axios';

export interface InstrumentBase {
    number: number;
    status: string;
    manufacturer?: string;
    commissioning_year?: number;
    ownership_document?: string;
    location?: string;
    notes?: string;
    custom_columns?: Record<string, any>;
}

export interface MeasuringInstrumentCreate extends InstrumentBase {
    characteristics?: string;
    name: string;
    measurement_range?: string;
    accuracy?: string;
    verification_certificate?: string;
    verification_date?: string;
    next_verification_date?: string;
    registration_number?: string;
    verification_method?: string;
}

export interface MeasuringInstrumentResponse extends MeasuringInstrumentCreate {
    id: number;
    laboratory_id: number;
}

export interface TestingInstrumentCreate {
    number: number;
    status: string;
    characteristics?: string;
    test_name: string;
    equipment_name: string;
    manufacturer?: string;
    technical_specs?: string;
    commissioning_year: number;
    document_info?: string;
    ownership_document?: string;
    location?: string;
    notes?: string;
    custom_columns?: Record<string, any>;
}

export interface TestingInstrumentUpdate {
    instrument_data: string;
    ownership_document?: File;
}

export interface TestingInstrumentResponse extends TestingInstrumentCreate {
    id: number;
    laboratory_id: number;
}

export interface HelpingInstrumentCreate extends InstrumentBase {
    name: string;
    purpose?: string;
}

export interface HelpingInstrumentResponse extends HelpingInstrumentCreate {
    id: number;
    laboratory_id: number;
}

export const instrumentsApi = {
    getMeasuringInstruments(laboratoryId: string): Promise<AxiosResponse<MeasuringInstrumentResponse[]>> {
        return axiosInstance.get(`/api/instruments/laboratory/${laboratoryId}/measuring`);
    },

    createMeasuringInstrument(laboratoryId: string, data: MeasuringInstrumentCreate): Promise<AxiosResponse<MeasuringInstrumentResponse>> {
        return axiosInstance.post(`/api/instruments/laboratory/${laboratoryId}/measuring`, data);
    },

    getTestingInstruments(laboratoryId: string): Promise<AxiosResponse<TestingInstrumentResponse[]>> {
        return axiosInstance.get(`/api/instruments/laboratory/${laboratoryId}/testing`);
    },

    createTestingInstrument(laboratoryId: string, data: FormData): Promise<AxiosResponse<TestingInstrumentResponse>> {
        return axiosInstance.post(`/api/instruments/laboratory/${laboratoryId}/testing`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    getHelpingInstruments(laboratoryId: string): Promise<AxiosResponse<HelpingInstrumentResponse[]>> {
        return axiosInstance.get(`/api/instruments/laboratory/${laboratoryId}/helping`);
    },

    createHelpingInstrument(laboratoryId: string, data: HelpingInstrumentCreate): Promise<AxiosResponse<HelpingInstrumentResponse>> {
        return axiosInstance.post(`/api/instruments/laboratory/${laboratoryId}/helping`, data);
    },

    deleteMeasuringInstrument: async (laboratoryId: string, instrumentId: number) => {
        const response = await axiosInstance.delete(`/api/instruments/laboratory/${laboratoryId}/measuring/${instrumentId}`);
        return response.data;
    },

    deleteTestingInstrument: async (laboratoryId: string, instrumentId: number) => {
        const response = await axiosInstance.delete(`/api/instruments/laboratory/${laboratoryId}/testing/${instrumentId}`);
        return response.data;
    },

    deleteHelpingInstrument: async (laboratoryId: string, instrumentId: number) => {
        const response = await axiosInstance.delete(`/api/instruments/laboratory/${laboratoryId}/helping/${instrumentId}`);
        return response.data;
    },

    updateMeasuringInstrument(laboratoryId: string, instrumentId: number, data: FormData): Promise<AxiosResponse<MeasuringInstrumentResponse>> {
        return axiosInstance.put(`/api/instruments/laboratory/${laboratoryId}/measuring/${instrumentId}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    updateTestingInstrument: async (laboratoryId: string | number, instrumentId: number, data: FormData): Promise<AxiosResponse> => {
        return axiosInstance.put(`/api/instruments/laboratory/${laboratoryId}/testing/${instrumentId}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    updateHelpingInstrument(laboratoryId: string, instrumentId: number, data: FormData): Promise<AxiosResponse<HelpingInstrumentResponse>> {
        return axiosInstance.put(`/api/instruments/laboratory/${laboratoryId}/helping/${instrumentId}`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
    },

    exportMeasuringInstruments: async (ids: number[], format: string) => {
        const params = new URLSearchParams();
        ids.forEach(id => params.append('instrument_ids', id.toString()));
        params.append('format', format);
        
        return await axiosInstance.get(`/api/instruments/measuring-instruments/export?${params.toString()}`, {
            responseType: 'blob'
        });
    },

    exportTestingInstruments: async (ids: number[], format: string) => {
        const params = new URLSearchParams();
        ids.forEach(id => params.append('instrument_ids', id.toString()));
        params.append('format', format);
        
        return await axiosInstance.get(`/api/instruments/testing-instruments/export?${params.toString()}`, {
            responseType: 'blob'
        });
    },

    exportHelpingInstruments: async (ids: number[], format: string) => {
        const params = new URLSearchParams();
        ids.forEach(id => params.append('instrument_ids', id.toString()));
        params.append('format', format);
        
        return await axiosInstance.get(`/api/instruments/helping-instruments/export?${params.toString()}`, {
            responseType: 'blob'
        });
    },

    downloadOwnershipDocument: async (laboratoryId: string | number, instrumentId: number): Promise<AxiosResponse<Blob>> => {
        return axiosInstance.get(`/api/instruments/laboratory/${laboratoryId}/testing/${instrumentId}/ownership-document`, {
            responseType: 'blob'
        });
    },

    downloadMeasuringDocument: async (laboratoryId: string | number, instrumentId: number): Promise<AxiosResponse<Blob>> => {
        const response = await axiosInstance.get(
            `/api/instruments/laboratory/${laboratoryId}/measuring/${instrumentId}/ownership-document`,
            { responseType: 'blob' }
        );
        return response;
    },

    downloadTestingDocument: async (laboratoryId: string | number, instrumentId: number): Promise<AxiosResponse<Blob>> => {
        const response = await axiosInstance.get(
            `/api/instruments/laboratory/${laboratoryId}/testing/${instrumentId}/ownership-document`,
            { responseType: 'blob' }
        );
        return response;
    },

    downloadHelpingDocument: async (laboratoryId: string | number, instrumentId: number): Promise<AxiosResponse<Blob>> => {
        const response = await axiosInstance.get(
            `/api/instruments/laboratory/${laboratoryId}/helping/${instrumentId}/ownership-document`,
            { responseType: 'blob' }
        );
        return response;
    }
};

export default instrumentsApi; 